import React, { useMemo } from 'react';
import { BRAND, INK, ELEVATION, RADIUS } from 'Utils/styleTokens';

// A compact bar strip of a data source's past profile runs. One bar per successful
// snapshot; bar height is proportional to that snapshot's total DB size, so the
// strip reads as "how the database grew/shrank across re-profiles". Newest is on the
// right. Clicking a bar selects that snapshot — the page re-renders its stats and
// tables from that run (onSelectBar / selectedJobId, both optional: without them the
// strip stays purely presentational).
//
// `history` is newest-first (the same order the caller derives it in); we reverse for
// display so time flows left→right. No data fetching.
const CHART_HEIGHT = 96;

function shortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  // e.g. "Jul 18" — enough to distinguish runs without crowding the axis.
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const ProfileHistoryTimeline = ({ history, formatBytes, formatInstant, selectedJobId, onSelectBar }) => {
  const bars = useMemo(() => (history || []).slice().reverse(), [history]);
  const maxSize = useMemo(
    () => bars.reduce((m, s) => Math.max(m, Number(s.totalSizeBytes) || 0), 0),
    [bars]
  );

  if (bars.length === 0) return null;

  const newestJobId = history[0] && history[0].jobId;
  // With no explicit selection the newest snapshot is the one the page shows.
  const viewedJobId = selectedJobId != null ? selectedJobId : newestJobId;
  const clickable = typeof onSelectBar === 'function';

  return (
    <div
      style={{
        marginTop: 24,
        padding: '16px 20px',
        border: `1px solid ${INK[150]}`,
        borderRadius: RADIUS.md,
        background: '#fff',
        boxShadow: ELEVATION.e1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <h3
          style={{
            margin: 0, fontSize: 12, fontWeight: 600, color: INK[400],
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}
        >
          Profile history
        </h3>
        <span style={{ fontSize: 11, color: INK[400] }}>
          {bars.length} {bars.length === 1 ? 'snapshot' : 'snapshots'} · bar height = total size
          {clickable && bars.length > 1 ? ' · click a bar to view that profile' : ''}
        </span>
      </div>
      <div
        style={{
          display: 'flex', alignItems: 'flex-end', gap: 8,
          height: CHART_HEIGHT, paddingBottom: 2,
        }}
      >
        {bars.map((s) => {
          const size = Number(s.totalSizeBytes) || 0;
          const pct = maxSize > 0 ? (size / maxSize) * 100 : 0;
          const isViewed = s.jobId === viewedJobId;
          const when = formatInstant ? formatInstant(s.snapshotTakenAt) : s.snapshotTakenAt;
          return (
            <div
              key={s.jobId}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              data-testid={`profile-history-bar-${s.jobId}`}
              onClick={clickable ? () => onSelectBar(s.jobId) : undefined}
              onKeyDown={clickable
                ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectBar(s.jobId); } }
                : undefined}
              title={`${when}\n${formatBytes(size)} · ${s.tableCount != null ? s.tableCount : '—'} tables${clickable && !isViewed ? '\nClick to view this profile' : ''}`}
              style={{
                flex: '1 1 0', minWidth: 8, maxWidth: 56,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-end', height: '100%',
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <span style={{ fontSize: 9, color: INK[400], marginBottom: 3, whiteSpace: 'nowrap' }}>
                {formatBytes(size)}
              </span>
              <div
                style={{
                  width: '100%',
                  height: `${Math.max(3, pct)}%`,
                  minHeight: 3,
                  borderRadius: '4px 4px 0 0',
                  background: isViewed ? BRAND.blue : 'rgba(49,93,255,0.28)',
                  outline: isViewed && s.jobId !== newestJobId ? `2px solid ${BRAND.blue}` : 'none',
                  outlineOffset: 1,
                }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        {bars.map((s) => (
          <div
            key={s.jobId}
            style={{
              flex: '1 1 0', minWidth: 8, maxWidth: 56, textAlign: 'center',
              fontSize: 9,
              color: s.jobId === viewedJobId ? INK[900] : INK[400],
              fontWeight: s.jobId === viewedJobId ? 600 : 400,
              whiteSpace: 'nowrap', overflow: 'hidden',
            }}
          >
            {shortDate(s.snapshotTakenAt)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileHistoryTimeline;
